import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, futureValueCompound } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// Compares Roth vs Traditional retirement contributions over a long horizon.
// Math:
//   Traditional: deduct contribution from current income (saving = contrib * currentRate).
//                FV taxed at retirement rate.
//   Roth:        contribution made post-tax (no current deduction).
//                FV is tax-free.
// Equivalent comparison requires investing the Traditional tax savings too.
// We do that here so the comparison is apples-to-apples.
export default function RothVsTraditionalPage() {
    const [annualContrib, setAnnualContrib] = useState(7000);
    const [years, setYears] = useState(30);
    const [returnRate, setReturnRate] = useState(7);
    const [currentRate, setCurrentRate] = useState(24);
    const [retirementRate, setRetirementRate] = useState(22);

    const result = useMemo(() => {
        const c = toNum(annualContrib);
        const yrs = Math.max(1, toNum(years));
        const r = toNum(returnRate);
        const curRate = toNum(currentRate) / 100;
        const retRate = toNum(retirementRate) / 100;
        const monthly = c / 12;

        // Roth: post-tax contribution, tax-free growth + withdrawals
        const rothFV = futureValueCompound(0, monthly, r, yrs);
        const rothAfterTax = rothFV; // already tax-free

        // Traditional: pre-tax contribution -> same gross FV but taxed at retirement
        const tradFV = futureValueCompound(0, monthly, r, yrs);
        const tradAfterTax = tradFV * (1 - retRate);

        // The fair comparison: Traditional allows you to invest the tax savings.
        // In Roth scenario, you contribute $c gross and pay current tax separately
        // (your take-home is reduced by c*curRate). In Traditional, the c is pre-tax,
        // so your take-home only drops by c*(1 - curRate). The DIFFERENCE — c*curRate —
        // is what you can invest in a taxable account in Trad scenario.
        // Most people don't actually do this — they just spend the difference. Show both.
        const tradTaxSavings = c * curRate;
        const sideAccountFV = futureValueCompound(0, tradTaxSavings / 12, r, yrs);
        // Simplified: assume LTCG 15% on side-account growth; cost basis = contributions
        const sideContribTotal = tradTaxSavings * yrs;
        const sideGain = Math.max(0, sideAccountFV - sideContribTotal);
        const sideAfterTax = sideContribTotal + sideGain * 0.85;
        const tradAfterTaxFair = tradAfterTax + sideAfterTax;

        const winner = rothAfterTax >= tradAfterTaxFair ? "roth" : "traditional";
        const margin = Math.abs(rothAfterTax - tradAfterTaxFair);

        return {
            rothFV,
            rothAfterTax,
            tradFV,
            tradAfterTax,
            tradAfterTaxFair,
            tradTaxSavings,
            sideAfterTax,
            winner,
            margin,
        };
    }, [annualContrib, years, returnRate, currentRate, retirementRate]);

    return (
        <CalculatorShell
            slug="roth-vs-traditional"
            explainer={
                                <>
                    <h2>How this Roth vs. Traditional calculator works</h2>
                    <p>
                        This calculator projects your after-tax retirement balance under both Roth and
                        Traditional contribution strategies given the same dollar contribution, so you can
                        compare the tax treatment at contribution time against the tax treatment at withdrawal.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Traditional contributions:</strong> pre-tax dollars go in, reducing taxable income today — but withdrawals in retirement are taxed as ordinary income.</li>
                        <li><strong>Roth contributions:</strong> post-tax dollars go in — growth and qualified withdrawals are completely tax-free.</li>
                        <li><strong>Same rate, same outcome:</strong> if your marginal tax rate at contribution equals your rate at withdrawal, both accounts produce identical after-tax wealth.</li>
                        <li><strong>Tax-deferred vs. tax-free growth:</strong> Traditional growth is tax-deferred; Roth growth is tax-free — both avoid annual tax drag on investment returns.</li>
                        <li><strong>Required Minimum Distributions (RMDs):</strong> Traditional accounts require distributions starting at age 73; Roth IRAs have no RMDs during the owner's lifetime.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Roth is generally better if you expect to be in a higher tax bracket in retirement than you are today.</li>
                        <li>Traditional is generally better if you expect to be in a lower bracket in retirement, deferring tax to when it costs less.</li>
                        <li>Younger, lower-earning workers are often in their lowest tax bracket now, making Roth contributions especially valuable early in a career.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Annual contribution" prefix="$" value={annualContrib} onChange={setAnnualContrib} dataTestId="calc-input-contrib" hint="2025 limits: $7,000 IRA, $23,500 401(k)" />
                    <Field label="Years until retirement" value={years} onChange={setYears} dataTestId="calc-input-years" />
                    <Field label="Expected return" suffix="%" value={returnRate} onChange={setReturnRate} dataTestId="calc-input-return" hint="S&P 500 long-term average ~7%" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Current tax rate" suffix="%" value={currentRate} onChange={setCurrentRate} dataTestId="calc-input-current-rate" />
                        <Field label="Retirement tax rate" suffix="%" value={retirementRate} onChange={setRetirementRate} dataTestId="calc-input-retire-rate" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label={result.winner === "roth" ? "Roth wins by" : "Traditional wins by"}
                        value={result.margin}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Roth — after tax", value: result.rothAfterTax, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-roth" },
                            { label: "Traditional — after tax (with side-fund)", value: result.tradAfterTaxFair, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-trad-fair" },
                            { label: "Traditional — after tax (no side-fund)", value: result.tradAfterTax, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-trad-naive" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
